import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { purchaseOrdersApi } from '../../api/endpoints';
import StatusBadge from '../../components/common/StatusBadge';
import {
  PurchaseOrderDetail,
  PurchaseOrderStatusMap,
} from '../../types';
import Select from '../../components/common/Select';
import {
  FiCheck, FiX, FiFileText, FiTruck,
  FiDollarSign, FiCalendar, FiMapPin, FiHash, FiClock,
  FiDatabase, FiUploadCloud,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { invoicesApi } from '../../api/endpoints';
import { SapSyncStatus } from '../../types';
import SapSyncBadge from '../../components/common/SapSyncBadge';
import ConfirmSapSyncDialog from '../../components/common/ConfirmSapSyncDialog';

const PurchaseOrderDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'items' | 'confirmations' | 'shipments' | 'invoices'>('items');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState<{
    status: string;
    notes: string;
    items: { itemId: string; confirmedQuantity: number }[];
  }>({ status: 'Accepted', notes: '', items: [] });
  const [submitting, setSubmitting] = useState(false);
  const [sapDialogOpen, setSapDialogOpen] = useState(false);
  const [sapSubmitting, setSapSubmitting] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await purchaseOrdersApi.get(id);
      setOrder(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('orders.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const openConfirmModal = useCallback(() => {
    if (!order) return;
    setConfirmData({
      status: 'Accepted',
      notes: '',
      items: order.items.map((item) => ({
        itemId: item.id,
        confirmedQuantity: item.orderedQuantity,
      })),
    });
    setShowConfirmModal(true);
  }, [order]);

  const handleConfirmSubmit = useCallback(async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await purchaseOrdersApi.confirm(id, confirmData);
      toast.success(t('orders.orderConfirmed'));
      setShowConfirmModal(false);
      fetchOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('orders.confirmError'));
    } finally {
      setSubmitting(false);
    }
  }, [id, confirmData, fetchOrder, t]);

  const handleConfirmSapSync = useCallback(async () => {
    if (!id) return;
    setSapSubmitting(true);
    try {
      const response = await purchaseOrdersApi.queueSapSyncSingle(id);
      const queued = response.data?.queued ?? 0;
      if (queued > 0) {
        toast.success(t('orders.sapSyncSuccessSingle'));
      } else {
        // Idempotent skip: backend reports 0 queued because the row was already
        // queued / synced. Surface as info rather than error.
        toast.info(t('orders.sapSyncSkipped', { count: 1 }));
      }
      setSapDialogOpen(false);
      fetchOrder();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('orders.sapSyncError'));
    } finally {
      setSapSubmitting(false);
    }
  }, [id, fetchOrder, t]);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('pl-PL', { style: 'currency', currency: 'EUR' });

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : null;

  const formatDateTime = (dateStr: string | null) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString('pl-PL', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      : null;

  /** Parse shipToAddress which may be a JSON string or plain string */
  const parsedAddress = useMemo(() => {
    if (!order?.shipToAddress) return null;
    try {
      const addr = typeof order.shipToAddress === 'string'
        ? JSON.parse(order.shipToAddress)
        : order.shipToAddress;
      const lines: string[] = [];
      if (addr.Name) lines.push(addr.Name);
      if (addr.AddressLine1) lines.push(addr.AddressLine1);
      if (addr.AddressLine2) lines.push(addr.AddressLine2);
      if (addr.AddressLine3) lines.push(addr.AddressLine3);
      const cityLine = [addr.PostalCode, addr.City].filter(Boolean).join(' ');
      if (cityLine) lines.push(cityLine);
      if (addr.StateOrRegion) lines.push(addr.StateOrRegion);
      if (addr.CountryCode) lines.push(addr.CountryCode);
      return lines.length > 0 ? lines : null;
    } catch {
      // Not JSON, just return as single line
      return [order.shipToAddress];
    }
  }, [order?.shipToAddress]);

  /** Derive marketplace from the PO number or sellingPartyId */
  const marketplaceInfo = useMemo(() => {
    if (!order) return null;
    const poNum = order.amazonPurchaseOrderNumber || '';
    // Common Amazon marketplace mapping by seller ID patterns or currency
    const marketplaces: Record<string, { flag: string; name: string }> = {
      'EUR': { flag: '\uD83C\uDDEA\uD83C\uDDFA', name: 'Amazon EU' },
      'GBP': { flag: '\uD83C\uDDEC\uD83C\uDDE7', name: 'Amazon.co.uk' },
      'USD': { flag: '\uD83C\uDDFA\uD83C\uDDF8', name: 'Amazon.com' },
      'PLN': { flag: '\uD83C\uDDF5\uD83C\uDDF1', name: 'Amazon.pl' },
      'SEK': { flag: '\uD83C\uDDF8\uD83C\uDDEA', name: 'Amazon.se' },
    };
    return marketplaces[order.currency] || { flag: '\uD83C\uDF10', name: 'Amazon' };
  }, [order]);

  /** Map sellingPartyId (AFN/MFN) to a nice fulfillment badge */
  const fulfillmentInfo = useMemo(() => {
    if (!order?.sellingPartyId) return null;
    const id = order.sellingPartyId.toUpperCase();
    if (id === 'AFN' || id.includes('FBA') || id.includes('AMAZON')) {
      return { label: t('orders.fulfillmentFBA'), icon: '\uD83C\uDFEC', variant: 'fba' as const };
    }
    if (id === 'MFN' || id.includes('MERCHANT') || id.includes('DEFAULT')) {
      return { label: t('orders.fulfillmentMFN'), icon: '\uD83D\uDCE6', variant: 'mfn' as const };
    }
    return { label: order.sellingPartyId, icon: '\uD83D\uDCE6', variant: 'mfn' as const };
  }, [order?.sellingPartyId, t]);

  /** Map numeric order status to a color class */
  const orderStatusColor = useMemo(() => {
    if (!order) return '';
    const colorMap: Record<number, string> = {
      0: 'status-new',        // Nowe
      1: 'status-info',       // Potwierdzone odbiór
      2: 'status-success',    // Potwierdzone
      3: 'status-warning',    // Częściowo potwierdzone
      4: 'status-danger',     // Odrzucone
      5: 'status-success',    // Wysłane
      6: 'status-warning',    // Częściowo wysłane
      7: 'status-success',    // Dostarczone
      8: 'status-danger',     // Anulowane
      9: 'status-muted',      // Zamknięte
    };
    return colorMap[order.status] || 'status-muted';
  }, [order]);

  const statusLabel = order ? (PurchaseOrderStatusMap[order.status] || `Status ${order.status}`) : '';

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">{t('orders.notFound')}</div>
        <Link to="/purchase-orders" className="btn btn-secondary">
          {t('orders.backToList')}
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/purchase-orders" className="btn btn-link">
            &larr; {t('orders.title')}
          </Link>
          <h1>{t('orders.orderTitle', { number: order.amazonPurchaseOrderNumber })}</h1>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={openConfirmModal}>
            <FiCheck /> {t('orders.confirmOrder')}
          </button>
          {/* SAP-sync button. Disabled when this PO is already queued or synced
              -- only NotSynced (0) is actionable; the badge in the info section
              tells the user the current state. */}
          <button
            type="button"
            className="btn"
            onClick={() => setSapDialogOpen(true)}
            disabled={order.sapSyncStatus !== SapSyncStatus.NotSynced || sapSubmitting}
            title={
              order.sapSyncStatus === SapSyncStatus.Queued
                ? t('orders.sapSyncQueued')
                : order.sapSyncStatus === SapSyncStatus.Synced
                  ? t('orders.sapSyncSynced')
                  : undefined
            }
            style={{
              backgroundColor: order.sapSyncStatus !== SapSyncStatus.NotSynced ? 'var(--bg-elevated)' : '#3b82f6',
              borderColor: order.sapSyncStatus !== SapSyncStatus.NotSynced ? 'var(--border)' : '#3b82f6',
              color: order.sapSyncStatus !== SapSyncStatus.NotSynced ? 'var(--text-muted)' : '#fff',
              fontWeight: 600,
              cursor: order.sapSyncStatus !== SapSyncStatus.NotSynced ? 'not-allowed' : 'pointer',
              opacity: order.sapSyncStatus !== SapSyncStatus.NotSynced ? 0.7 : 1,
            }}
          >
            <FiUploadCloud /> {t('orders.sapSyncButton')}
          </button>
          <button
            className="btn btn-secondary"
            onClick={async () => {
              if (!id) return;
              try {
                const res = await invoicesApi.generateFromPo(id);
                toast.success(t('orders.invoiceGenerated'));
                window.location.href = `/invoices/${res.data.id}`;
              } catch (err: any) {
                toast.error(err.response?.data?.message || t('orders.invoiceGenerateError'));
              }
            }}
          >
            <FiFileText /> {t('orders.generateInvoice')}
          </button>
        </div>
      </div>

      <ConfirmSapSyncDialog
        isOpen={sapDialogOpen}
        loading={sapSubmitting}
        orders={[{
          id: order.id,
          amazonPurchaseOrderNumber: order.amazonPurchaseOrderNumber,
          status: order.status,
          channel: order.channel,
          marketplace: order.marketplace ?? null,
          shipToCountry: null,
          totalAmount: order.totalAmount,
          currency: order.currency,
        }]}
        statusLabel={(s) => PurchaseOrderStatusMap[s] || `Status ${s}`}
        onCancel={() => { if (!sapSubmitting) setSapDialogOpen(false); }}
        onConfirm={handleConfirmSapSync}
      />

      {/* ── Status banner ─────────────────────── */}
      <div className={`po-status-banner po-${orderStatusColor}`}>
        <div className="po-status-banner-left">
          <span className={`po-status-dot po-${orderStatusColor}`} />
          <span className="po-status-label">{statusLabel}</span>
        </div>
        <div className="po-status-banner-badges">
          {marketplaceInfo && (
            <span className="po-badge po-badge-marketplace">
              <span className="po-badge-icon">{marketplaceInfo.flag}</span>
              {marketplaceInfo.name}
            </span>
          )}
          <StatusBadge type="channel" value={order.channel} />
          {fulfillmentInfo && (
            <span className={`po-badge po-badge-${fulfillmentInfo.variant}`}>
              <span className="po-badge-icon">{fulfillmentInfo.icon}</span>
              {fulfillmentInfo.label}
            </span>
          )}
        </div>
      </div>

      {/* ── Detail sections ───────────────────── */}
      <div className="po-detail-sections">

        {/* Order Info */}
        <div className="po-section">
          <div className="po-section-header">
            <FiHash className="po-section-icon" />
            <h3>{t('orders.sectionOrderInfo')}</h3>
          </div>
          <div className="po-section-grid">
            <div className="po-field">
              <span className="po-field-label">{t('orders.poNumber')}</span>
              <span className="po-field-value po-field-mono">{order.amazonPurchaseOrderNumber}</span>
            </div>
            <div className="po-field">
              <span className="po-field-label">{t('orders.orderDate')}</span>
              <span className="po-field-value">
                <FiCalendar className="po-field-inline-icon" />
                {formatDate(order.orderDate) || <span className="po-na">{t('orders.notAvailable')}</span>}
              </span>
            </div>
            <div className="po-field">
              <span className="po-field-label">{t('orders.channel')}</span>
              <span className="po-field-value">
                <StatusBadge type="channel" value={order.channel} />
              </span>
            </div>
            {fulfillmentInfo && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.fulfillment')}</span>
                <span className="po-field-value">
                  <span className={`po-badge po-badge-${fulfillmentInfo.variant}`}>
                    <span className="po-badge-icon">{fulfillmentInfo.icon}</span>
                    {fulfillmentInfo.label}
                  </span>
                </span>
              </div>
            )}
            {marketplaceInfo && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.marketplace')}</span>
                <span className="po-field-value">
                  <span className="po-badge po-badge-marketplace">
                    <span className="po-badge-icon">{marketplaceInfo.flag}</span>
                    {marketplaceInfo.name}
                  </span>
                </span>
              </div>
            )}
            {/* SAP sync status -- visible regardless of state so the user always
                knows where this PO is in the SAP flow. */}
            <div className="po-field">
              <span className="po-field-label">{t('orders.sapSyncStatus')}</span>
              <span className="po-field-value">
                <SapSyncBadge status={order.sapSyncStatus} compact={false} />
              </span>
            </div>
            {order.sapQueuedAt && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.sapQueuedAt')}</span>
                <span className="po-field-value po-field-small">
                  <FiClock className="po-field-inline-icon" />
                  {formatDateTime(order.sapQueuedAt)}
                  {order.sapQueuedBy && (
                    <span style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>
                      ({order.sapQueuedBy})
                    </span>
                  )}
                </span>
              </div>
            )}
            {order.sapSyncedAt && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.sapSyncedAt')}</span>
                <span className="po-field-value po-field-small">
                  <FiCheck className="po-field-inline-icon" style={{ color: '#10b981' }} />
                  {formatDateTime(order.sapSyncedAt)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Shipping Info */}
        <div className="po-section">
          <div className="po-section-header">
            <FiTruck className="po-section-icon" />
            <h3>{t('orders.sectionShipping')}</h3>
          </div>
          <div className="po-section-grid">
            {formatDate(order.expectedDeliveryDate) && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.expectedDelivery')}</span>
                <span className="po-field-value">
                  <FiCalendar className="po-field-inline-icon" />
                  {formatDate(order.expectedDeliveryDate)}
                </span>
              </div>
            )}
            {(formatDate(order.shipWindowStart) || formatDate(order.shipWindowEnd)) && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.shipWindow')}</span>
                <span className="po-field-value">
                  <FiClock className="po-field-inline-icon" />
                  {formatDate(order.shipWindowStart) || '?'} &ndash; {formatDate(order.shipWindowEnd) || '?'}
                </span>
              </div>
            )}
            {order.shipToPartyId && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.shipTo')}</span>
                <span className="po-field-value po-field-mono">{order.shipToPartyId}</span>
              </div>
            )}
            {order.billToPartyId && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.billTo')}</span>
                <span className="po-field-value po-field-mono">{order.billToPartyId}</span>
              </div>
            )}
            {parsedAddress && (
              <div className="po-field po-field-wide">
                <span className="po-field-label">
                  <FiMapPin className="po-field-inline-icon" />
                  {t('orders.shippingAddress')}
                </span>
                <div className="po-address-block">
                  {parsedAddress.map((line, i) => (
                    <div key={i} className="po-address-line">{line}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Financial Info */}
        <div className="po-section">
          <div className="po-section-header">
            <FiDollarSign className="po-section-icon" />
            <h3>{t('orders.sectionFinancial')}</h3>
          </div>
          <div className="po-section-grid">
            <div className="po-field">
              <span className="po-field-label">{t('orders.amount')}</span>
              <span className="po-field-value po-field-amount">{formatCurrency(order.totalAmount)}</span>
            </div>
            <div className="po-field">
              <span className="po-field-label">{t('orders.currency')}</span>
              <span className="po-field-value">
                <span className="po-badge po-badge-currency">{order.currency}</span>
              </span>
            </div>
          </div>
        </div>

        {/* System Info */}
        <div className="po-section po-section-muted">
          <div className="po-section-header">
            <FiDatabase className="po-section-icon" />
            <h3>{t('orders.sectionSystem')}</h3>
          </div>
          <div className="po-section-grid">
            {formatDateTime(order.syncedAt) && (
              <div className="po-field">
                <span className="po-field-label">{t('orders.syncDate')}</span>
                <span className="po-field-value po-field-small">{formatDateTime(order.syncedAt)}</span>
              </div>
            )}
            <div className="po-field">
              <span className="po-field-label">{t('orders.createdAt')}</span>
              <span className="po-field-value po-field-small">{formatDateTime(order.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          {t('orders.items')} ({order.items.length})
        </button>
        <button
          className={`tab ${activeTab === 'confirmations' ? 'active' : ''}`}
          onClick={() => setActiveTab('confirmations')}
        >
          {t('orders.confirmations')} ({order.confirmations.length})
        </button>
        <button
          className={`tab ${activeTab === 'shipments' ? 'active' : ''}`}
          onClick={() => setActiveTab('shipments')}
        >
          {t('orders.shipments')} ({order.shipments.length})
        </button>
        <button
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          {t('orders.invoices')} ({order.invoices.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'items' && (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('orders.product')}</th>
                  <th>ASIN</th>
                  <th>{t('orders.ordered')}</th>
                  <th>{t('orders.unitPrice')}</th>
                  <th>{t('orders.value')}</th>
                  <th>{t('orders.confirmed')}</th>
                  <th>{t('orders.shipped')}</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, idx) => (
                  <tr key={item.id}>
                    <td>{item.itemSequenceNumber}</td>
                    <td>{item.productTitle}</td>
                    <td>{item.asin || '-'}</td>
                    <td>
                      {item.orderedQuantity} {item.quantityUnitOfMeasure}
                    </td>
                    <td>{formatCurrency(item.unitPrice)}</td>
                    <td>{formatCurrency(item.totalPrice)}</td>
                    <td>{item.confirmedQuantity ?? '-'}</td>
                    <td>{item.shippedQuantity ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'confirmations' && (
          <div className="table-responsive">
            {order.confirmations.length === 0 ? (
              <p className="text-muted">{t('orders.noConfirmations')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('common.status')}</th>
                    <th>{t('orders.confirmationDate')}</th>
                    <th>{t('orders.confirmedBy')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.confirmations.map((conf) => (
                    <tr key={conf.id}>
                      <td>
                        <span className="badge">{conf.status}</span>
                      </td>
                      <td>{formatDate(conf.confirmationDate)}</td>
                      <td>{conf.confirmedBy || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'shipments' && (
          <div className="table-responsive">
            {order.shipments.length === 0 ? (
              <p className="text-muted">{t('orders.noShipments')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('shipments.amazonShipmentId')}</th>
                    <th>{t('common.status')}</th>
                    <th>{t('shipments.shipDate')}</th>
                    <th>{t('shipments.carrier')}</th>
                    <th>{t('shipments.tracking')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {order.shipments.map((ship) => (
                    <tr key={ship.id}>
                      <td>{ship.amazonShipmentId || '-'}</td>
                      <td>
                        <span className="badge">{ship.status}</span>
                      </td>
                      <td>{formatDate(ship.shipDate)}</td>
                      <td>{ship.carrierName || '-'}</td>
                      <td>{ship.trackingNumber || '-'}</td>
                      <td>
                        <Link to={`/shipments/${ship.id}`} className="btn btn-sm btn-link">
                          {t('common.details')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="table-responsive">
            {order.invoices.length === 0 ? (
              <p className="text-muted">{t('orders.noInvoices')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('invoices.invoiceNumber')}</th>
                    <th>{t('common.status')}</th>
                    <th>{t('invoices.invoiceDate')}</th>
                    <th>{t('orders.amount')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {order.invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td>{inv.invoiceNumber}</td>
                      <td>
                        <span className="badge">{inv.status}</span>
                      </td>
                      <td>{formatDate(inv.invoiceDate)}</td>
                      <td>{formatCurrency(inv.totalAmount)}</td>
                      <td>
                        <Link to={`/invoices/${inv.id}`} className="btn btn-sm btn-link">
                          {t('common.details')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('orders.confirmOrder')}</h2>
              <button
                className="btn btn-icon"
                onClick={() => setShowConfirmModal(false)}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('orders.confirmationStatus')}</label>
                <Select
                  options={[
                    { value: 'Accepted', label: t('orders.accepted') },
                    { value: 'Rejected', label: t('orders.rejected') },
                  ]}
                  value={confirmData.status}
                  onChange={(value) =>
                    setConfirmData((prev) => ({ ...prev, status: value }))
                  }
                />
              </div>

              <div className="form-group">
                <label>{t('orders.items')}</label>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('orders.product')}</th>
                        <th>{t('orders.ordered')}</th>
                        <th>{t('orders.confirmed')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, idx) => (
                        <tr key={item.id}>
                          <td>{item.productTitle}</td>
                          <td>{item.orderedQuantity}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control"
                              min={0}
                              max={item.orderedQuantity}
                              value={confirmData.items[idx]?.confirmedQuantity ?? 0}
                              onChange={(e) => {
                                const newItems = [...confirmData.items];
                                newItems[idx] = {
                                  ...newItems[idx],
                                  confirmedQuantity: Number(e.target.value),
                                };
                                setConfirmData((prev) => ({
                                  ...prev,
                                  items: newItems,
                                }));
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="form-group">
                <label>{t('orders.notes')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={confirmData.notes}
                  onChange={(e) =>
                    setConfirmData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder={t('orders.notesPlaceholder')}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmSubmit}
                disabled={submitting}
              >
                {submitting ? t('orders.submitting') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetailPage;
